import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, eur } from "@/pages/mrv/mrvApi";
import OutcomeBadge from "@/pages/rc/OutcomeBadge";
import MRVLayout from "@/pages/mrv/MRVLayout";
import { Button } from "@/components/ui/button";
import { FileStack, FilePlus2, Lock, Mail, Plus } from "lucide-react";

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-sm p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-stone-400">{label}</span>
        <Icon className="w-4 h-4 text-stone-300" strokeWidth={1.5} />
      </div>
      <div className="font-display text-3xl font-semibold text-stone-900 mt-3">{value}</div>
    </div>
  );
}

export default function MRVDashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <MRVLayout>
      <div data-testid="mrv-dashboard-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-900">Dashboard</h1>
            <p className="text-stone-500 text-sm mt-1">Panoramica preventivi Multirischi Volontariato</p>
          </div>
          <Button data-testid="mrv-dashboard-new-quote" onClick={() => navigate("/multirischi-volontariato/preventivi/nuovo")} className="rounded-sm bg-stone-900 hover:bg-stone-800">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Nuovo Preventivo
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat label="Totale Preventivi" value={stats?.total ?? "—"} icon={FileStack} testid="mrv-stat-total" />
          <Stat label="Da Completare" value={stats?.draft ?? "—"} icon={FilePlus2} testid="mrv-stat-draft" />
          <Stat label="Inviati" value={stats?.emailed ?? "—"} icon={Mail} testid="mrv-stat-emailed" />
          <Stat label="Riservati Direzione" value={stats?.reserved ?? "—"} icon={Lock} testid="mrv-stat-reserved" />
        </div>

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
          <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
            <h2 className="font-display text-lg font-medium text-stone-900">Ultimi Preventivi</h2>
            <Link to="/multirischi-volontariato/preventivi" data-testid="mrv-view-all-quotes" className="text-xs text-stone-500 hover:text-stone-900">
              Vedi tutti →
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-100 text-stone-500 text-xs uppercase tracking-wide">
                <th className="text-left font-medium px-5 py-2.5">Numero</th>
                <th className="text-left font-medium px-5 py-2.5">Ente</th>
                <th className="text-left font-medium px-5 py-2.5">Premio</th>
                <th className="text-left font-medium px-5 py-2.5">Esito</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recent || []).map((q) => (
                <tr key={q.id} data-testid={`mrv-dashboard-quote-row-${q.id}`}
                  onClick={() => navigate(`/multirischi-volontariato/preventivi/${q.id}`)}
                  className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer">
                  <td className="px-5 py-3 font-mono text-stone-700">{q.quote_number}</td>
                  <td className="px-5 py-3 text-stone-700">{q.organization_name || "—"}</td>
                  <td className="px-5 py-3 font-mono">{eur(q.premium_result?.gross_total)}</td>
                  <td className="px-5 py-3"><OutcomeBadge status={q.calc_status} /></td>
                </tr>
              ))}
              {stats?.recent?.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-stone-400">Nessun preventivo. Creane uno nuovo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MRVLayout>
  );
}
